import React, { useState, useEffect } from "react";
import {
  Card,
  Tag,
  Button,
  Icon,
  Typography,
  Spin,
  Empty,
  Modal,
  Input,
  Tooltip,
  notification,
  Row,
  Col,
  Badge
} from "antd";
import api from "../../services/api";
import moment from "moment";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// Mapeamento de Status do ClickUp para Cores (Nomes exatos conforme configurado no ClickUp)
const STATUS_COLORS = {
  "para fazer": "geekblue",
  "em progresso": "#fa8c16",
  "deploy": "#13c2c2",
  "pendente de confirmação": "#722ed1", // Roxo para validação
  "finalizada": "#52c41a", // Verde
  "bloqueado": "#f5222d"
};

const STATUS_TRANSLATIONS = {
  "para fazer": "Para Fazer",
  "em progresso": "Em Progresso",
  "deploy": "Em Deploy",
  "pendente de confirmação": "Pendente de Confirmação",
  "finalizada": "Finalizada",
  "bloqueado": "Bloqueado"
};

const Tarefas = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [comment, setComment] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      // Busca tarefas da lista específica via Proxy
      const response = await api.get("/acordo/clickup/tasks/");
      if (response.data && Array.isArray(response.data.tasks)) {
        setTasks(response.data.tasks);
      }
    } catch (error) {
      console.error("Erro ao buscar tarefas:", error);
      notification.error({ message: "Não foi possível carregar suas solicitações." });
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (task, type) => {
    setCurrentTask(task);
    setActionType(type);
    setComment("");
    setModalVisible(true);
  };

  const confirmAction = async () => {
    if (!currentTask) return;
    setActionLoading(true);

    try {
      let newStatus = "";
      if (actionType === "approve") {
        newStatus = "finalizada";
      } else {
        newStatus = "para fazer";
      }

      // 1. Atualizar Status
      await api.post(`/acordo/clickup/tasks/${currentTask.id}/action/`, {
        action: actionType,
        comment: comment
      });

      notification.success({
        message: actionType === "approve" ? "Solicitação finalizada!" : "Solicitação reaberta."
      });

      setModalVisible(false);
      fetchTasks();

    } catch (error) {
      console.error("Erro na ação:", error);
      notification.error({ message: "Erro ao atualizar a tarefa. Verifique se o status existe no ClickUp." });
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusTag = (statusObj) => {
    const status = statusObj.status.toLowerCase();
    const color = STATUS_COLORS[status] || "default";
    const label = STATUS_TRANSLATIONS[status] || statusObj.status;
    return <Tag color={color} style={{ fontWeight: 'bold' }}>{label.toUpperCase()}</Tag>;
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2} style={{ marginBottom: 0 }}>Minhas Solicitações</Title>
          <Text type="secondary">Acompanhe e valide a resolução dos seus reportes.</Text>
        </div>
        <Button icon="reload" onClick={fetchTasks} loading={loading} size="large">Atualizar</Button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 50 }}>
          <Spin size="large" />
        </div>
      ) : tasks.length === 0 ? (
        <Empty
          description="Nenhum reporte encontrado."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button type="primary" onClick={() => document.querySelector('.ant-btn-circle').click()}>
            Criar Novo Reporte
          </Button>
        </Empty>
      ) : (
        <Row gutter={[24, 24]}>
          {tasks.map(task => (
            <Col xs={24} md={12} lg={8} key={task.id}>
              <Card
                hoverable
                style={{ height: '100%', borderRadius: '8px', overflow: 'hidden' }}
                bodyStyle={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column' }}
                actions={
                  task.status.status.toLowerCase() === 'pendente de confirmação' ? [
                    <Tooltip title="Resolver definitivamente">
                      <Button type="link" icon="check-circle" style={{ color: '#52c41a' }} onClick={() => handleAction(task, 'approve')}>
                        VALIDAR
                      </Button>
                    </Tooltip>,
                    <Tooltip title="Recusar e reabrir">
                      <Button type="link" icon="close-circle" style={{ color: '#f5222d' }} onClick={() => handleAction(task, 'reject')}>
                        REABRIR
                      </Button>
                    </Tooltip>
                  ] : null
                }
              >
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {getStatusTag(task.status)}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {moment(parseInt(task.date_created)).format("DD/MM/YYYY")}
                  </Text>
                </div>

                <Title level={4} style={{ fontSize: 16, marginBottom: 12, fontWeight: 700 }}>{task.name}</Title>

                <div style={{ flex: 1 }}>
                  <Paragraph ellipsis={{ rows: 3 }} type="secondary" style={{ fontSize: 13 }}>
                    {task.text_content || "Sem descrição detalhada."}
                  </Paragraph>
                </div>

                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Tag color="blue" style={{ borderRadius: '10px' }}>#{task.id}</Tag>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Modal
        title={actionType === "approve" ? "✅ Confirmar Resolução" : "❌ Reabrir para Ajustes"}
        visible={modalVisible}
        onOk={confirmAction}
        onCancel={() => setModalVisible(false)}
        confirmLoading={actionLoading}
        okText={actionType === "approve" ? "Confirmar e Finalizar" : "Reabrir Solicitação"}
        okType={actionType === "approve" ? "primary" : "danger"}
        centered
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>Tarefa:</Text> <Text>{currentTask && currentTask.name}</Text>
        </div>
        <p>
          {actionType === "approve"
            ? "Ao confirmar, esta solicitação será marcada como concluída definitivamente."
            : "Explique o que ainda precisa ser ajustado para que possamos corrigir:"}
        </p>
        <TextArea
          rows={4}
          placeholder={actionType === "approve" ? "Algum comentário sobre a solução? (Opcional)" : "Motivo da reabertura (Obrigatório)"}
          value={comment}
          onChange={e => setComment(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default Tarefas;



// WEBPACK FOOTER //
// ./src/pages/Tarefas/index.js